// import API
import { mutations, queryKeys } from 'RestAPIs/rapini';

// import components
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import { Button } from '@mui/material';
import { ControlledReadOnlyField } from 'components/shared/ControlledFields/ControlledReadOnlyField';

// import constants
import { employeeDetailsFields } from './EndOfEmploymentSettlementsForm.consts';

// import external libraries
import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// import styles
import { StyledFooterFormActionsBox } from './EndOfEmploymentSettlementsForm.styles';

// import types
import {
  EndOfEmploymentSettlementsFormData,
  EndOfEmploymentSettlementsFormProps,
  SettlementFormValues
} from './EndOfEmploymentSettlementsForm.types';

// import utils
import { getDefaultValues } from './EndOfEmploymentSettlementsForm.utils';
import SelectSettlementType from './SelectSettlementType';
import { useQueryClient } from 'react-query';
import showSnackbar from 'components/shared/Snackbar';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { yupResolver } from '@hookform/resolvers/yup';
import { endOfEmploymentSchema } from './EndOfEmploymentSettlementsForm.schema';

dayjs.extend(customParseFormat);

export const EndOfEmploymentSettlementsForm: FC<
  EndOfEmploymentSettlementsFormProps
> = ({
  endOfEmploymentData,
  isLoading,
  onClose,
  showPlusMinusHoursReversed = false
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'endOfEmploymentSettlement'
  });
  const [disableSubmit, setDisableSubmit] = useState(false);

  const queryClient = useQueryClient();
  const methods = useForm<SettlementFormValues>({
    defaultValues: getDefaultValues(
      endOfEmploymentData,
      showPlusMinusHoursReversed
    ),
    mode: 'onChange',
    resolver: yupResolver(endOfEmploymentSchema),
    context: { endOfEmploymentData }
  });

  const { control, reset, watch } = methods;
  const watchEndOfEmploymentSettlementType = watch(
    'endOfEmploymentSettlementType'
  );
  const isTransferSettlementType =
    watchEndOfEmploymentSettlementType === 'TRANSFER';

  const {
    mutateAsync: processEndOfEmploymentSettlement,
    isLoading: isLoadingProcessEndOfEmploymentSettlement
  } = mutations.useProcessEndOfEmploymentSettlement();

  const onSubmit = async (data: EndOfEmploymentSettlementsFormData) => {
    const employmentId = endOfEmploymentData?.employment.id;

    if (!employmentId) {
      showSnackbar({
        severity: 'error',
        message: t('somethingWentWrong', { keyPrefix: '' })
      });
      return;
    }

    const payload = {
      employmentId: endOfEmploymentData.employment.id,
      endOfEmploymentSettlementDate: dayjs(
        data.endOfEmploymentSettlementDate,
        'DD-MM-YYYY'
      ).format('YYYY-MM-DD'),
      type: data.endOfEmploymentSettlementType,
      ...(data.validEmployment &&
        isTransferSettlementType && {
          transferEmploymentId: +data.validEmployment
        })
    };

    try {
      setDisableSubmit(true);
      await processEndOfEmploymentSettlement(payload);
      await queryClient.invalidateQueries(
        queryKeys.getEndOfEmploymentSettlementOverview()
      );
      setDisableSubmit(false);
      onClose();
    } catch (_) {
      showSnackbar({
        severity: 'error',
        message: t('somethingWentWrong', { keyPrefix: '' })
      });
    }
  };

  useEffect(() => {
    reset(getDefaultValues(endOfEmploymentData, showPlusMinusHoursReversed));
  }, [endOfEmploymentData, showPlusMinusHoursReversed, reset]);

  return (
    <MultiStepForm onSubmit={methods.handleSubmit(onSubmit)} methods={methods}>
      <MultiStepForm.Section title={t('employeeDetails')}>
        {employeeDetailsFields.map(({ name, labelKey }) => (
          <ControlledReadOnlyField
            key={name}
            control={control}
            name={name}
            label={t(labelKey)}
            isLoading={isLoading}
          />
        ))}
      </MultiStepForm.Section>
      <MultiStepForm.Section title={t('selectSettlementType')}>
        <SelectSettlementType
          endOfEmploymentData={endOfEmploymentData}
          isLoading={isLoading}
        />
      </MultiStepForm.Section>
      <MultiStepForm.Actions>
        <StyledFooterFormActionsBox>
          <Button onClick={onClose} color="secondary" variant="outlined">
            {t('cancel', { keyPrefix: '' })}
          </Button>
          <Button
            loading={isLoadingProcessEndOfEmploymentSettlement}
            type="submit"
            variant="contained"
            autoFocus
            color="primary"
            disabled={disableSubmit}
          >
            {disableSubmit
              ? t('loading', { keyPrefix: '' })
              : t('save', { keyPrefix: '' })}
          </Button>
        </StyledFooterFormActionsBox>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};
